// The Overview's view of one specialist's actions, in its page's own tables: every
// external deadline that has passed, then its top next action (one per area on Building
// readiness), first in the same Priority order as its page's table.
// See docs/Y2026-Q4/design-structured-next-actions-and-dates.md 7.1.
import { useMemo } from "react";

import {
  AREA_LABEL,
  AREA_ORDER,
} from "../api/hooks/buildingReadiness";
import type {
  NextAction,
  ProjectAgentStatus,
  ProjectAgentStatusList,
  TrackedDate,
} from "../api/hooks/useProjectAgentStatus";
import {
  prioritised,
  projectSource,
} from "../utils/nextActions";
import { ActionTable } from "./NextActionTable";
import { DateTable } from "./TrackedDateTable";
import styles from "./TopNextActions.module.css";

export const TopNextActions = ({
  entry,
  data,
  projectId,
}: {
  entry: ProjectAgentStatus;
  data: ProjectAgentStatusList;
  projectId: string;
}) => {
  const today = data.lists_today;
  const byArea = entry.keeps_readiness;
  const ordered = useMemo(
    () => prioritised(entry.lists?.actions ?? []),
    [entry],
  );
  const top = useMemo(
    () =>
      new Set(
        (byArea
          ? AREA_ORDER.map((area) => ordered.find((a) => a.area === area))
          : ordered.slice(0, 1)
        ).flatMap((a) => (a ? [a.action_id] : [])),
      ),
    [ordered, byArea],
  );
  const isTop = useMemo(
    () => (action: NextAction) => top.has(action.action_id),
    [top],
  );
  const isMissed = useMemo(
    () => (d: TrackedDate) => d.hardness === "hard" && !!d.date && d.date < today,
    [today],
  );
  const anyMissed = (entry.lists?.tracked_dates ?? []).some(
    (d) => !d.closed && isMissed(d),
  );
  const quietAreas = byArea
    ? AREA_ORDER.filter((area) => !ordered.some((a) => a.area === area))
    : [];

  if (top.size === 0 && !anyMissed)
    return <p className={styles.muted}>Nothing open.</p>;

  return (
    <div className={styles.tables}>
      {anyMissed && (
        <div>
          <p className={styles.label}>Missed deadlines</p>
          <DateTable
            agent={entry.agent}
            projectId={projectId}
            data={data}
            include={isMissed}
            empty=""
            collapseCovered={false}
            showArea={byArea}
          />
        </div>
      )}
      {top.size > 0 && (
        <div>
          <p className={styles.label}>
            {byArea ? "Top next action per area" : "Top next action"}
          </p>
          <ActionTable
            scope={{ kind: "agent", agent: entry.agent }}
            sources={[projectSource(projectId, data)]}
            include={isTop}
            empty=""
            controls={false}
            showArea={byArea}
            anchored
          />
        </div>
      )}
      {quietAreas.length > 0 && (
        <p className={styles.muted}>
          Nothing open in {quietAreas.map((area) => AREA_LABEL[area]).join(", ")}.
        </p>
      )}
    </div>
  );
};
